/**
 * Who a new Team member is: name, email, and the username suggested from it.
 * These are pure functions with no dependencies, so `npm run test:unit` exercises them with
 * Node's runner.
 *
 * WHY THIS EXISTS (2026-10-07, found in the live test of !214): the sign-in service requires
 * an email, first name and last name on every account. A person created without them was
 * stopped at their first sign-in by the sign-in service's own "Update account information"
 * page, straight after being handed a temporary password. The dialog now asks for whatever
 * the server says is required (GET /operators/preflight -> `required_fields`, read from the
 * realm). The server enforces the same rule and answers 400 if anything slips through.
 */

/** The profile fields the Team dialog collects, in the order it shows them. */
export type ProfileField = "first_name" | "last_name" | "email";
export const PROFILE_FIELDS: readonly ProfileField[] = ["first_name", "last_name", "email"];

/**
 * Used while preflight is loading, or when an older server does not report
 * `required_fields`. It matches the realm as read on 2026-10-07 and the server's own fallback.
 * Failing towards "required" costs one more field to fill in. Failing towards "optional"
 * costs a person stuck on the sign-in service's profile page.
 */
export const REQUIRED_FALLBACK: readonly ProfileField[] = ["email", "first_name", "last_name"];

/** Plain-language labels, matching the server's 400 messages. */
export const FIELD_LABEL: Record<ProfileField, string> = {
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
};

/** The required fields the server reported, or the fallback. Unknown names are ignored. */
export function requiredFields(preflight?: { required_fields?: string[] | null } | null): Set<ProfileField> {
  const reported = preflight?.required_fields;
  if (!Array.isArray(reported)) return new Set(REQUIRED_FALLBACK);
  return new Set(reported.filter((f): f is ProfileField => (PROFILE_FIELDS as readonly string[]).includes(f)));
}

/**
 * The same deliberately simple check as the server: one @, no whitespace, a dot in the
 * domain, at most 254 characters. It catches typos ("jo@example", "jo example.org"). It is
 * not a full RFC 5322 parser, and does not try to be.
 */
export function isValidEmail(email: string): boolean {
  const e = email.trim();
  return e.length <= 254 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
}

export type ProfileValues = Record<ProfileField, string>;
export type ProfileErrors = Partial<Record<ProfileField, string>>;

/** Per-field messages for the form. An empty object means the profile is complete. */
export function profileErrors(values: ProfileValues, required: Set<ProfileField>): ProfileErrors {
  const out: ProfileErrors = {};
  for (const f of PROFILE_FIELDS) {
    const v = (values[f] ?? "").trim();
    if (!v && required.has(f)) {
      out[f] = f === "email" ? "Enter their email address." : `Enter their ${FIELD_LABEL[f].toLowerCase()}.`;
    } else if (v.length > 255 && f !== "email") {
      out[f] = `${FIELD_LABEL[f]} can be at most 255 characters.`;
    }
  }
  const email = (values.email ?? "").trim();
  if (email && !isValidEmail(email)) out.email = "Enter a full email address, like j.okafor@example.com.";
  return out;
}

/* ------------------------------------------------------------------------------------- *
 * WHO MAY GRANT WHAT, AND TO WHICH CUSTOMERS (owner decisions of 2026-10-08, MR !221;
 * docs/design/msp-platform-admin-split.md section 1.3). The server enforces every rule
 * here; these helpers make the dialog never offer what the server would refuse, and never
 * send a grant that silently drops something.
 *
 * Kept free of imports (roles are passed in as the ordered tuple) so Node's test runner can
 * load this file directly.
 * ------------------------------------------------------------------------------------- */

/** The Default pool (device quarantine org). Platform admins only (decision 4). */
export const DEFAULT_ORG_ID = 1;

/** Ids arrive as strings or numbers depending on the endpoint; compare as numbers. */
export type AnyId = string | number;
const num = (id: AnyId): number => Number(id);

function rankIn(order: readonly string[], role: string | null | undefined): number {
  return role ? order.indexOf(role) : -1;
}

/**
 * Roles the caller may offer in the Team dialog.
 *  - never above the caller's own role, and never EQUAL to it unless the caller is a
 *    platform admin (decision D-03, 2026-10-09, OP #1696: a peer you create is a peer you
 *    cannot then manage). Server: "cannot grant a role equal to or above your own";
 *  - MSP admin and above only for a platform admin (decision 2: MSP admins do not create
 *    or manage each other).
 */
export function grantableRoles<R extends string>(
  order: readonly R[],
  myRole: R | null,
  isPlatformAdmin: boolean,
): R[] {
  if (!myRole) return [];
  const mine = rankIn(order, myRole);
  const msp = rankIn(order, "msp_admin");
  return order.filter((r) => {
    const rank = rankIn(order, r);
    if (rank > mine) return false;
    // D-03: strictly below your own role, except for a platform admin.
    if (rank === mine && !isPlatformAdmin) return false;
    if (msp >= 0 && rank >= msp && !isPlatformAdmin) return false;
    return true;
  });
}

/**
 * May the caller edit / reset / remove this person? Mirrors the server's
 * `_outranks_target`: a platform admin always; anyone else only for a STRICTLY lower role
 * (so an MSP admin never manages a peer MSP admin, decision 2). An account with no known
 * role ranks as the top role (fail closed, as on the server).
 */
export function canManageMember(
  order: readonly string[],
  myRole: string | null,
  isPlatformAdmin: boolean,
  targetRole: string | null,
): boolean {
  if (isPlatformAdmin) return true;
  const target = targetRole ? rankIn(order, targetRole) : order.length - 1;
  return target < rankIn(order, myRole);
}

/** Customers the editor may put someone in: its own, never the Default pool (decision 4). */
export function pickableOrgIds(myOrgIds: readonly AnyId[]): number[] {
  return [...new Set(myOrgIds.map(num))].filter((id) => id !== DEFAULT_ORG_ID);
}

/**
 * The dialog's starting customer selection.
 *  - edit: the operator's REAL customers that the editor can pick (not the screen's current
 *    customer -- that was the decision-6 bug);
 *  - create: the current customer, when it is pickable (never the Default pool).
 */
export function initialOrgSelection(opts: {
  operatorOrgIds?: readonly AnyId[] | null;
  currentOrgId: AnyId | null;
  pickable: readonly number[];
}): number[] {
  if (opts.operatorOrgIds) {
    return opts.operatorOrgIds.map(num).filter((id) => opts.pickable.includes(id));
  }
  const cur = opts.currentOrgId == null ? NaN : num(opts.currentOrgId);
  return opts.pickable.includes(cur) ? [cur] : [];
}

/**
 * The org_ids to send (decision 6). Customers the editor cannot pick are carried over
 * verbatim -- whether the editor cannot see them, or they are the Default pool an older
 * account still holds. Customers it can pick follow the selection. The server applies the
 * same carry-over, so this is belt and braces, not the only guard.
 *
 * A platform admin's grant is global: new ones carry no customers, and an edit leaves
 * whatever the account already has untouched.
 */
export function buildOrgGrant(opts: {
  role: string;
  operatorOrgIds?: readonly AnyId[] | null;
  pickable: readonly number[];
  selected: readonly number[];
}): number[] {
  const existing = (opts.operatorOrgIds ?? []).map(num);
  if (opts.role === "platform_admin") return existing;
  const chosen = opts.selected.filter((id) => opts.pickable.includes(id));
  const kept = existing.filter((id) => !opts.pickable.includes(id) && !chosen.includes(id));
  return [...chosen, ...kept];
}

/**
 * The site_ids to send. Only site-confined roles carry sites. A site whose customer is still
 * granted stays; a site of a customer just removed goes (the server would refuse it); a
 * site the editor cannot see at all is carried over untouched.
 */
export function buildSiteGrant(opts: {
  siteScoped: boolean;
  siteIds: readonly AnyId[];
  knownSites: readonly { id: AnyId; org_id: AnyId }[];
  orgIds: readonly number[];
}): number[] {
  if (!opts.siteScoped) return [];
  const out: number[] = [];
  for (const raw of opts.siteIds) {
    const id = num(raw);
    const site = opts.knownSites.find((s) => num(s.id) === id);
    if ((!site || opts.orgIds.includes(num(site.org_id))) && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * A username suggestion from an email address: the part before the @, lowercased, keeping
 * only characters that are safe in a username (a-z 0-9 . _ -). Leading and trailing
 * punctuation is trimmed. Returns "" when nothing usable is left. The admin can always
 * overwrite it. The dialog stops suggesting as soon as they do.
 */
export function suggestUsername(email: string): string {
  const local = email.trim().split("@")[0] ?? "";
  return local
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "")
    .replace(/^[._-]+|[._-]+$/g, "")
    .slice(0, 64);
}
